import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
  OnDestroy,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import { StaffLayoutStyle, UserTimelines } from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  SharedComponentsModule,
  UserDetailsModule,
} from '@wilson/shared-components';
import {
  PreferredSettingsStateFacadeService,
  TimelineInteractionService,
} from '@wilson/shift-timeline/services';
import { ShiftTimelineSettingsState } from '@wilson/shift-timeline/state';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Observable } from 'rxjs';
import { TimelineUserDetailsComponent } from '../timeline-user-details/timeline-user-details.component';
import { UserTimelinesInfoCardKpisComponent } from '../user-timelines-info-card-kpis/user-timelines-info-card-kpis.component';

@Component({
  selector: 'wilson-user-timelines-info-card',
  templateUrl: './user-timelines-info-card.component.html',
  styleUrls: ['./user-timelines-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SharedComponentsModule,
    NzTooltipModule,
    NzSkeletonModule,
    UserDetailsModule,
    AuthorizationModule,
    FeatureFlagsModule,
    NzDividerModule,
    UserTimelinesInfoCardKpisComponent,
    TimelineUserDetailsComponent,
  ],
})
export class UserTimelinesInfoCardComponent implements OnInit, OnDestroy {
  timelineInteractionService = inject(TimelineInteractionService);
  private store = inject(Store);
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  @Input() user!: UserTimelines;
  @Input() index!: number;

  protected staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  protected showOvertimeAccountKpi$!: Observable<boolean>;
  protected showProductivityKpi$!: Observable<boolean>;
  protected showWorkTimeWeekKpi$!: Observable<boolean>;
  protected showWorkTimeMonthKpi$!: Observable<boolean>;
  protected showPlannedTimeWeekKpi$!: Observable<boolean>;
  protected showPlannedTimeMonthKpi$!: Observable<boolean>;
  protected showOvertimeWeekKpi$!: Observable<boolean>;
  protected showOvertimeMonthKpi$!: Observable<boolean>;
  protected showHomeLocation$!: Observable<boolean>;
  protected showVacationBalance$!: Observable<boolean>;
  protected defaultRowHeight$ = this.store.select(
    ShiftTimelineSettingsState.defaultRowHeight,
  );
  protected StaffLayoutStyle = StaffLayoutStyle;

  ngOnInit(): void {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      this.showOvertimeAccountKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showOvertimeAccountKpi(userId),
      );
      this.showProductivityKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showProductivityKpi(userId),
      );
      this.showWorkTimeWeekKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showWorkTimeWeekKpi(userId),
      );
      this.showWorkTimeMonthKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showWorkTimeMonthKpi(userId),
      );
      this.showPlannedTimeWeekKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showPlannedTimeWeekKpi(userId),
      );
      this.showPlannedTimeMonthKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showPlannedTimeMonthKpi(userId),
      );
      this.showOvertimeWeekKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showOvertimeWeekKpi(userId),
      );
      this.showOvertimeMonthKpi$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showOvertimeMonthKpi(userId),
      );
      this.showHomeLocation$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showHomeLocation(userId),
      );
      this.showVacationBalance$ = this.store.select(
        ShiftTimelinePreferredSettingsState.showVacationBalance(userId),
      );
    }
    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;
  }

  ngOnDestroy(): void {
    // Flush to prevent memory leak
    this.staffLayoutStyle$ = null as any;
    this.showOvertimeAccountKpi$ = null as any;
    this.showProductivityKpi$ = null as any;
    this.showWorkTimeWeekKpi$ = null as any;
    this.showWorkTimeMonthKpi$ = null as any;
    this.showPlannedTimeWeekKpi$ = null as any;
    this.showPlannedTimeMonthKpi$ = null as any;
    this.showOvertimeWeekKpi$ = null as any;
    this.showOvertimeMonthKpi$ = null as any;
    this.showHomeLocation$ = null as any;
    this.showVacationBalance$ = null as any;
  }
}
